<template>
  <div class="px-4">
    <div id="unevenGridContainer" class="flex flex-col w-full mx-auto bg-white">
      <div
        id="directoryPreview"
        class="flex flex-col items-center pt-2 pr-2 pl-3.5 text-xs whitespace-nowrap text-zinc-700 max-md:pr-5"
      >
        <!-- Browser Header -->
        <div class="flex flex-col bg-gray-200 w-full rounded-t-lg px-2">
          <div class="flex flex-row justify-start gap-2 ml-2 pt-2 pt-3">
            <img
              loading="lazy"
              src="../../../../assets/expert-program/directory/traffic-lights.svg"
              class="object-fit shrink-0 self-stretch my-auto"
              alt=""
            />
            <div
              class="flex flex-row bg-white rounded-t-2xl w-full justify-between max-w-xs p-1"
            >
              <div class="flex flex-row justify-start items-center">
                <div class="ml-2">
                  <img
                    loading="lazy"
                    :src="logoFromWebsite(userStore.companyWebsite || '')"
                    class="w-8 h-8 mr-2 rounded-full"
                  />
                </div>
                <div>
                  <span>{{ userStore.companyName }}</span>
                </div>
              </div>
              <div class="flex flex-row justify-end items-center mr-3">
                <div v-html="CLOSE_ICON_SM"></div>
              </div>
            </div>
            <div class="flex flex-row items-center ml-2">
              <div v-html="PLUS_ICON" class="text-gray-600"></div>
            </div>
          </div>
        </div>

        <!-- Browser URL Bar -->
        <div class="flex flex-col w-full">
          <div class="flex flex-row justify-start items-center gap-2 ml-2 py-2">
            <img
              loading="lazy"
              src="../../../../assets/expert-program/directory/browser-actions.svg"
              class="object-fit shrink-0 self-stretch my-auto"
              alt=""
            />
            <div
              class="flex flex-row bg-gray-100 w-full p-1 rounded-full items-center whitespace-nowrap overflow-ellipsis overflow-hidden"
            >
              <div v-html="LOCK_ICON" class="text-gray-600 ml-2"></div>
              <div class="ml-2 overflow-hidden">
                <span
                  >{{
                    layout.websiteUrl || userStore.companyWebsite
                  }}/experts</span
                >
              </div>
            </div>
            <div>
              <div v-html="BROWSER_MENU_ICON" class="text-gray-600 ml-2"></div>
            </div>
          </div>
        </div>

        <!-- Directory Header -->
        <div
          class="flex flex-col gap-1 text-base justify-center items-center self-stretch py-6 mx-auto text-violet-600 rounded-md w-full max-w-full"
        >
          <DirectoryHeader
            v-if="layout.directoryHeaderCosmetic.isVisible"
            :boldness="layout.directoryHeaderCosmetic.bold"
            :fontFamily="layout.directoryHeaderCosmetic.fontFamily"
            :textColor="layout.directoryHeaderCosmetic.textColor"
            :backgroundColor="layout.directoryHeaderCosmetic.backgroundColor"
            :headerFontSize="layout.directoryHeaderCosmetic.headerFontSize"
            :paragraphFontSize="
              layout.directoryHeaderCosmetic.paragraphFontSize
            "
            :headlineText="layout.directoryHeaderCosmetic.headerText"
            :paragraphText="layout.directoryHeaderCosmetic.paragraphText"
          />
        </div>

        <!-- search filter + profile cards -->
        <div class="flex flex-row flex-1 w-full">
          <div class="flex flex-col items-center">
            <StaticSearchFilters
              :isEnabled="layout.showSearchFilters"
              :textColor="layout.directoryCosmetic.textColor"
              :backgroundColor="layout.directoryCosmetic.backgroundColor"
            />
          </div>

          <!-- Profile Cards -->
          <div class="w-full overflow-hidden">
            <div
              v-if="getLayoutSettings(layout).isGridUneven"
              class="flex-1 flex gap-2 bg-gray-100 p-6 justify-center"
              :style="{
                transform:
                  expandedLayoutId === layout.id && layout.layoutType === 'row'
                    ? 'scale(0.5)'
                    : 'none',
                transformOrigin: 'left top',
                width:
                  expandedLayoutId === layout.id && layout.layoutType === 'row'
                    ? '200%'
                    : '100%',
              }"
            >
              <div
                v-for="n in getLayoutSettings(layout).columns"
                :key="n"
                class="space-y-3"
              >
                <ProfileCard
                  v-for="profile in getProfilesForLayout(layout).filter(
                    (_, index) =>
                      index % getLayoutSettings(layout).columns === n - 1
                  )"
                  :key="profile.id"
                  :profile="profile"
                  :isCentered="getLayoutSettings(layout).profileCenterAligned"
                  :isGridUneven="getLayoutSettings(layout).isGridUneven"
                  :profileImageClass="
                    getLayoutSettings(layout).profileImageClass
                  "
                  :profileImageAndNameLayoutClass="
                    getLayoutSettings(layout).profileImageAndNameLayoutClass
                  "
                  :fontFamily="layout.directoryCosmetic.fontFamily"
                  :boldness="layout.directoryCosmetic.bold"
                  :textColor="layout.directoryCosmetic.textColor"
                  :backgroundColor="layout.directoryCosmetic.backgroundColor"
                  :top3MeetingsExperts="profiles.slice(0, 3)"
                  :fontSize="layout.directoryCosmetic.fontSize"
                />
              </div>
            </div>
            <div v-else class="w-full overflow-hidden">
              <div
                class="flex flex-wrap gap-2 bg-gray-100 p-6 justify-center"
                :style="{
                  transform:
                    expandedLayoutId === layout.id &&
                    layout.layoutType === 'row'
                      ? 'scale(0.5)'
                      : 'none',
                  transformOrigin: 'left top',
                  width:
                    expandedLayoutId === layout.id &&
                    layout.layoutType === 'row'
                      ? '200%'
                      : '100%',
                }"
              >
                <ProfileCard
                  v-for="profile in getProfilesForLayout(layout)"
                  :key="profile.id"
                  :profile="profile"
                  :isCentered="getLayoutSettings(layout).profileCenterAligned"
                  :isGridUneven="getLayoutSettings(layout).isGridUneven"
                  :profileImageClass="
                    getLayoutSettings(layout).profileImageClass
                  "
                  :profileImageAndNameLayoutClass="
                    getLayoutSettings(layout).profileImageAndNameLayoutClass
                  "
                  :fontFamily="layout.directoryCosmetic.fontFamily"
                  :boldness="layout.directoryCosmetic.bold"
                  :textColor="layout.directoryCosmetic.textColor"
                  :backgroundColor="layout.directoryCosmetic.backgroundColor"
                  :top3MeetingsExperts="profiles.slice(0, 3)"
                  :fontSize="layout.directoryCosmetic.fontSize"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import DirectoryHeader from "@/components/expert-program/DirectoryHeader.vue";
import ProfileCard from "@/components/expert-program/directory/ProfileCard.vue";
import StaticSearchFilters from "./StaticSearchFilters.vue";
import {
  DirectoryLayout,
  ApprovedExpert,
  Profile,
} from "@/types/dashboard-types";
import { directoryProfiles } from "@/data/expert-program/directory-preview-profiles";
import {
  CLOSE_ICON_SM,
  PLUS_ICON,
  LOCK_ICON,
  BROWSER_MENU_ICON,
} from "@/assets/svg/shared/svg-constants";

export default defineComponent({
  name: "DirectoryPreview",
  components: {
    DirectoryHeader,
    ProfileCard,
    StaticSearchFilters,
  },
  props: {
    layout: {
      type: Object as () => DirectoryLayout,
      required: true,
    },
    profiles: {
      type: Array as () => Profile[],
      required: true,
    },
    approvedExperts: {
      type: Array as () => ApprovedExpert[],
      required: true,
    },
    userStore: {
      type: Object,
      required: true,
    },
    expandedLayoutId: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const logoFromWebsite = (websiteUrl: string): string => {
      const urlTemplate = `https://img.logo.dev/{domain}?token=${process.env.VUE_APP_LOGO_API_KEY}`;
      const placeholder = "{domain}";

      try {
        const url = new URL(websiteUrl);
        const domain = url.hostname.split(".").slice(-2).join(".");

        return urlTemplate.replace(placeholder, domain);
      } catch (error) {
        return urlTemplate.replace(placeholder, "www.logo.com");
      }
    };

    const getLayoutSettings = (layout: DirectoryLayout) => {
      const settings = {
        profileImageClass: "rounded-full",
        profileCardsGap: "gap-3",
        profileImageAndNameLayoutClass: "flex-row",
        profileCenterAligned: false,
        columns: 5,
        isGridUneven: false,
      };

      // Apply template-specific settings
      if (layout.style === "template-1" || layout.style === "template-4") {
        settings.profileImageClass = "rounded-full";
      } else {
        settings.profileImageClass = "rounded-md";
      }

      if (layout.style === "template-3") {
        settings.profileCardsGap = "gap-0.5";
      }

      if (layout.style === "template-3" || layout.style === "template-5") {
        settings.profileImageAndNameLayoutClass = "flex-col";
      }

      if (layout.style === "template-5") {
        settings.profileCenterAligned = true;
      }

      return settings;
    };

    // Helper to get the correct profiles for preview based on layout type
    function getProfilesForLayout(layout: DirectoryLayout): Profile[] {
      const maxProfiles =
        layout.layoutType === "single"
          ? 1
          : layout.layoutType === "row"
          ? 3
          : 6; // Grid layout shows max 6 experts

      // For single/row layouts with specific expert selection
      if (
        (layout.layoutType === "single" || layout.layoutType === "row") &&
        !layout.selectRandomExperts
      ) {
        // Get unique selected experts in order
        const selectedExpertIds = [...new Set(layout.selectedExpertIds || [])];

        // If no experts are selected, show dummy profiles
        if (selectedExpertIds.length === 0) {
          return props.profiles.slice(0, maxProfiles);
        }

        // Map selected experts to Profile format
        const mappedSelectedExperts = selectedExpertIds
          .map((expertId) => {
            const expert = props.approvedExperts.find((e) => e.id === expertId);
            if (!expert) return null;

            return {
              id: expert.id,
              firstName: expert.firstName,
              lastName: expert.lastName,
              email: expert.email || "",
              title: expert.title || "",
              companyName: expert.companyName || "",
              imageUrl: expert.profile_picture || "",
              linkedinUrl: expert.linkedin_profile || "",
              country: expert.country || "",
              uuid: expert.uuid || expert.id,
              slug: expert.slug || "",
              meetingsCount: expert.meetingsCount || 0,
              spokenLanguages: expert.spokenLanguages || [],
              anyPastMeetings: expert.anyPastMeetings || false,
              soonestAvailabilities: expert.soonestAvailabilities || [],
              userProgramProfile: expert.userProgramProfiles?.map(
                (profile) => ({
                  expertise: profile.expertise || 0,
                  experience: profile.experience || 0,
                })
              ) || [{ expertise: 0, experience: 0 }],
            };
          })
          .filter((expert): expert is Profile => expert !== null);

        // Fill remaining slots with dummy profiles if needed
        const remainingCount = maxProfiles - mappedSelectedExperts.length;
        if (remainingCount > 0) {
          return [
            ...mappedSelectedExperts,
            ...props.profiles.slice(0, remainingCount),
          ];
        }
        return mappedSelectedExperts.slice(0, maxProfiles);
      }

      // For grid layout or random mode in single/row
      // Map approved experts to Profile format
      const mappedApprovedExperts = props.approvedExperts.map((expert) => {
        return {
          id: expert.id,
          firstName: expert.firstName,
          lastName: expert.lastName,
          email: expert.email || "",
          title: expert.title || "",
          companyName: expert.companyName || "",
          imageUrl: expert.profile_picture || "",
          linkedinUrl: expert.linkedin_profile || "",
          country: expert.country || "",
          uuid: expert.uuid || expert.id,
          slug: expert.slug || "",
          meetingsCount: expert.meetingsCount || 0,
          spokenLanguages: expert.spokenLanguages || [],
          anyPastMeetings: expert.anyPastMeetings || false,
          soonestAvailabilities: expert.soonestAvailabilities || [],
          userProgramProfile: expert.userProgramProfiles?.map((profile) => ({
            expertise: profile.expertise || 0,
            experience: profile.experience || 0,
          })) || [{ expertise: 0, experience: 0 }],
        };
      });

      // Take up to maxProfiles approved experts
      const approvedExpertsToShow = mappedApprovedExperts.slice(0, maxProfiles);

      // If we have less than maxProfiles approved experts, fill with dummy profiles
      const remainingCount = maxProfiles - approvedExpertsToShow.length;
      if (remainingCount > 0) {
        return [
          ...approvedExpertsToShow,
          ...props.profiles.slice(0, remainingCount),
        ];
      }
      return approvedExpertsToShow;
    }

    return {
      logoFromWebsite,
      getLayoutSettings,
      getProfilesForLayout,
      CLOSE_ICON_SM,
      PLUS_ICON,
      LOCK_ICON,
      BROWSER_MENU_ICON,
    };
  },
});
</script>
